import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl'
import { ScrollView } from 'react-native'

import { skipToken } from '@reduxjs/toolkit/query'
import { sortBy } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { CONVERSATION_TYPE } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { Divider } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/divider/Divider'
import { ChevronIconDirection } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/ChevronIcon'
import { ChevronV2Icon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/ChevronV2Icon'
import { CircleSlashIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/CircleSlashIcon'
import { SparkleSingleIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/SparkleSingleIcon'
import { LoadingSkeleton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/loadingSkeleton/LoadingSkeleton'
import { OrDivider } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/orDivider/OrDivider'
import { PressableOpacity } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/pressableOpacity/PressableOpacity'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import MessengerSummaryEssentials from './MessengerSummaryEssentials'
import { mixpanelEvents } from '../../../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../../../mixpanel/mixpanelTracking'
import { useSelectedConversation } from '../../../data/hooks/useSelectedConversation'
import { LyraAISummary, LyraAISummaryPromptType, useGetLyraAISummariesQuery } from '../../../data/lyraAISummariesApi'
import { getAiConversationMixpanelProperties } from '../../../data/mixpanelUtils'
import useAISummariesPanelData from '../../../data/useAISummariesPanelData'

type MessengerSummariesSideBarProps = {
  clientName?: string
  conversationType?: CONVERSATION_TYPE
}

const Container = styled.View({
  flex: 1,
  flexDirection: 'column',
})

const HeaderSection = styled.View(({ theme }) => ({
  paddingTop: theme.spacing['24px'],
  paddingHorizontal: theme.spacing['16px'],
  gap: theme.spacing['2px'],
}))

const AIBadgeRow = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['4px'],
  marginTop: theme.spacing['2px'],
}))

const ContentContainer = styled.View(({ theme }) => ({
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
  paddingHorizontal: theme.spacing['16px'],
}))

const LoadingContainer = styled.View({
  paddingHorizontal: 16,
})

const SummariesContentContainer = styled.View({
  flex: 1,
})

const SummaryItemContainer = styled.View({
  paddingHorizontal: 16,
})

const SummaryHeaderRow = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  paddingLeft: 8,
  paddingRight: 8,
  paddingTop: 16,
  paddingBottom: 8,
})

const SummaryDateContainer = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  flex: 1,
  gap: 8,
})

const ChevronContainer = styled.View({
  width: 24,
  height: 24,
  padding: 6,
  justifyContent: 'center',
  alignItems: 'center',
  marginLeft: 8,
})

const SummaryTextContainer = styled.View({
  paddingLeft: 8,
  paddingBottom: 16,
})

const FooterSection = styled.View(({ theme }) => ({
  paddingHorizontal: theme.spacing['16px'],
  paddingBottom: theme.spacing['24px'],
  gap: theme.spacing['4px'],
}))

const FooterRow = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['4px'],
}))

const formatSummaryDate = (timestamp: string): Date => new Date(timestamp)

const dateFormatOptions: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' }

type DateRangeFormatter = Intl.DateTimeFormat & {
  formatRangeToParts: (start: Date, end: Date) => Array<{ type: string; value: string; source: string }>
}

const formatSummaryDateLabel = (summary: LyraAISummary, locale: string): string => {
  const startDate = formatSummaryDate(summary.start_message_timestamp)
  const endDate = summary.end_message_timestamp ? formatSummaryDate(summary.end_message_timestamp) : null
  const formatter = new Intl.DateTimeFormat(locale, dateFormatOptions) as DateRangeFormatter

  if (!endDate) {
    return formatter.format(startDate)
  }

  const parts = formatter.formatRangeToParts(startDate, endDate)
  const isSameMonth = parts.some((part) => part.type === 'month' && part.source === 'shared')

  return parts
    .map((part) => {
      if (isSameMonth && part.type === 'literal' && part.source === 'shared' && part.value.includes('–')) {
        return '–'
      }
      return part.value
    })
    .join('')
}

const MessengerSummariesSideBar: React.FC<MessengerSummariesSideBarProps> = ({ clientName, conversationType }) => {
  const { colors } = useTheme()
  const { formatMessage, locale } = useIntl()
  const { lastAppointmentDate } = useAISummariesPanelData()
  const formattedLastSessionDate = lastAppointmentDate ? new Date(lastAppointmentDate) : null
  const selectedConversation = useSelectedConversation()
  const dbConversationId = selectedConversation?.db_conversation_id
  const scrollViewRef = useRef<ScrollView>(null)

  const { data: summaries, isLoading } = useGetLyraAISummariesQuery(
    dbConversationId && lastAppointmentDate
      ? {
          conversationIds: [dbConversationId],
          startTimestamp: lastAppointmentDate,
          promptType: LyraAISummaryPromptType.V1,
        }
      : skipToken,
  )

  const sortedSummaries = useMemo(
    () => (summaries ? sortBy(summaries, (s) => new Date(s.start_message_timestamp).getTime()) : []),
    [summaries],
  )

  const hasSummaries = sortedSummaries.length > 0

  const [expandedIndices, setExpandedIndices] = useState<Set<number>>(new Set())
  const hasInitializedExpansion = useRef(false)
  const shouldScrollToEnd = useRef(false)

  useEffect(() => {
    hasInitializedExpansion.current = false
    shouldScrollToEnd.current = false
    setExpandedIndices(new Set())
  }, [dbConversationId])

  useEffect(() => {
    if (hasSummaries && !hasInitializedExpansion.current) {
      setExpandedIndices(new Set([sortedSummaries.length - 1]))
      hasInitializedExpansion.current = true
      shouldScrollToEnd.current = true
    }
  }, [hasSummaries, sortedSummaries.length, dbConversationId])

  const toggleExpanded = useCallback((index: number) => {
    setExpandedIndices((prev) => {
      const next = new Set(prev)
      if (next.has(index)) {
        next.delete(index)
      } else {
        next.add(index)
      }
      return next
    })
  }, [])

  const mostRecentSummaryDate = hasSummaries
    ? formatSummaryDate(sortedSummaries[sortedSummaries.length - 1].start_message_timestamp)
    : null

  const renderSummaryItem = (summary: LyraAISummary, index: number) => {
    const isExpanded = expandedIndices.has(index)

    return (
      <SummaryItemContainer key={`${summary.start_message_timestamp}-${index}`}>
        <PressableOpacity
          onPress={() => {
            if (!expandedIndices.has(index)) {
              track({
                event: mixpanelEvents.OPEN_AI_CHAT_SUMMARY,
                properties: getAiConversationMixpanelProperties(conversationType),
                details: { date: summary.start_message_timestamp },
              })
            }
            toggleExpanded(index)
          }}
          testID={`SummaryHeader-${index}`}
          pressedOpacity={0.8}
          accessibilityLabel={formatMessage(
            {
              defaultMessage: 'Toggle summary for {date}',
              description: 'Accessibility label for expanding/collapsing a conversation summary',
            },
            { date: formatSummaryDateLabel(summary, locale) },
          )}
        >
          <SummaryHeaderRow>
            <SummaryDateContainer>
              <Title
                size={TitleSize.XSMALL}
                color={colors.textPrimary}
                text={formatSummaryDateLabel(summary, locale)}
              />
            </SummaryDateContainer>
            <ChevronContainer>
              <ChevronV2Icon
                size={12}
                direction={isExpanded ? ChevronIconDirection.UP : ChevronIconDirection.DOWN}
                fillColor={colors.textPrimary}
              />
            </ChevronContainer>
          </SummaryHeaderRow>
        </PressableOpacity>
        {isExpanded && (
          <SummaryTextContainer>
            <BodyText size={BodyTextSize.DEFAULT} color={colors.textSecondary} text={summary.summary.summary_text} />
            <MessengerSummaryEssentials resources={summary.summary.lyra_library_resources} />
          </SummaryTextContainer>
        )}
      </SummaryItemContainer>
    )
  }

  return (
    <Container testID={tID('MessengerSummariesSideBar')}>
      <HeaderSection>
        <Title
          size={TitleSize.LARGE}
          text={
            <FormattedMessage
              defaultMessage='Review conversation summaries'
              description='Header for the Lyra AI summaries side panel'
            />
          }
        />
        {formattedLastSessionDate && (
          <BodyText
            size={BodyTextSize.SMALL}
            color={colors.textSecondary}
            text={
              <FormattedMessage
                defaultMessage='since last session on {lastSessionDate}'
                description='Subheader showing time since last session'
                values={{
                  lastSessionDate: (
                    <FormattedDate value={formattedLastSessionDate} month='short' day='numeric' year='numeric' />
                  ),
                }}
              />
            }
          />
        )}
        <AIBadgeRow>
          <SparkleSingleIcon size={18} color={colors.textSecondary} />
          <BodyText
            size={BodyTextSize.CAPTION}
            color={colors.textSecondary}
            bold
            text={
              <FormattedMessage defaultMessage='AI Generated' description='Badge indicating AI generated content' />
            }
          />
        </AIBadgeRow>
      </HeaderSection>

      {isLoading && (
        <>
          <Divider height={1} color={colors.dividerSecondary} style={{ marginTop: 32 }} />
          <LoadingContainer style={{ marginTop: 32 }}>
            <LoadingSkeleton widthPercentage={60} height={24} />
            <LoadingSkeleton widthPercentage={91} height={16} marginTop={16} />
            <LoadingSkeleton widthPercentage={100} height={16} marginTop={8} />
            <LoadingSkeleton widthPercentage={83} height={16} marginTop={8} />
            <LoadingSkeleton widthPercentage={97} height={16} marginTop={8} />
          </LoadingContainer>
        </>
      )}

      {!isLoading && !hasSummaries && (
        <ContentContainer>
          <BodyText
            size={BodyTextSize.LARGE}
            color={colors.textSecondary}
            textAlign='center'
            text={
              <FormattedMessage
                defaultMessage='Summaries are generated once daily after {clientName} engages with Lyra AI'
                description='Empty state message when there are no Lyra AI summaries'
                values={{ clientName }}
              />
            }
          />
        </ContentContainer>
      )}

      {!isLoading && hasSummaries && (
        <SummariesContentContainer>
          <OrDivider
            text={
              <FormattedMessage
                defaultMessage='{count, plural, one {# conversation} other {# conversations}}'
                description='Number of conversation summaries'
                values={{ count: sortedSummaries.length }}
              />
            }
            textSize={BodyTextSize.CAPTION}
            textBold
            textColor={colors.textSecondary}
            lineColor={colors.dividerSecondary}
            lineHeight={1}
            marginVertical={32}
            textPaddingHorizontal={8}
            style={{ marginHorizontal: 16 }}
          />
          <ScrollView
            ref={scrollViewRef}
            style={{ flex: 1 }}
            onContentSizeChange={() => {
              if (shouldScrollToEnd.current) {
                shouldScrollToEnd.current = false
                scrollViewRef.current?.scrollToEnd({ animated: true })
              }
            }}
          >
            {sortedSummaries.map((summary, index) => (
              <React.Fragment key={`summary-${index}`}>
                {renderSummaryItem(summary, index)}
                {index < sortedSummaries.length - 1 && (
                  <Divider height={1} color={colors.dividerSecondary} style={{ marginHorizontal: 16 }} />
                )}
              </React.Fragment>
            ))}
          </ScrollView>
        </SummariesContentContainer>
      )}

      {!isLoading && hasSummaries && (
        <FooterSection>
          <FooterRow>
            <CircleSlashIcon size={14} fillColor={colors.textSecondary} />
            <BodyText
              size={BodyTextSize.SMALL}
              color={colors.textSecondary}
              text={
                <FormattedMessage
                  defaultMessage='Last updated on {date}'
                  description='Last updated timestamp for summaries'
                  values={{
                    date: mostRecentSummaryDate ? (
                      <FormattedDate value={mostRecentSummaryDate} month='short' day='numeric' year='numeric' />
                    ) : (
                      ''
                    ),
                  }}
                />
              }
            />
          </FooterRow>
          <BodyText
            size={BodyTextSize.SMALL}
            color={colors.textSecondary}
            text={
              <FormattedMessage
                defaultMessage='Lyra AI conversation summaries will be cleared after your next session with {clientName}'
                description='Message about when summaries will be cleared'
                values={{ clientName }}
              />
            }
          />
        </FooterSection>
      )}
    </Container>
  )
}

export default MessengerSummariesSideBar
